import { z } from 'zod/v3';
import { ResourceType } from '@cotera/api';
import { CocoEvent } from '@cotera/client/app/etc';
import { ok } from 'neverthrow';
import { v4 } from 'uuid';
import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import {
  resolveResourceNavigator,
  type ResourceNavigator,
} from '../resources/navigation';

type Params = {
  /** When set, new document will be assigned to this folder when first saved. Passed in nav state. */
  folderId?: string;
};

export class NewDocumentAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'New Document';
  description = 'Create a new document';
  inputSchema = z.object({
    folderId: z.string().uuid().optional(),
  });
  group = 'Document';
  icon = 'file-text';

  private _lastCreatedDocumentId: string | null = null;

  constructor(private readonly fallbackNavigator: ResourceNavigator) {
    super();
  }

  applicable(_context: ApplicableContext): boolean {
    return true;
  }

  async execute(
    params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const documentId = v4();
    resolveResourceNavigator(context, this.fallbackNavigator).open({
      resourceType: ResourceType.Document,
      resourceId: documentId,
      // The document doesn't exist server-side yet, so the surface has to be
      // told not to fetch it — via nav state on a page, via payload in a panel.
      payload: {
        isNew: true,
        ...(params.folderId !== undefined && { folderId: params.folderId }),
      },
      state: {
        new: true,
        ...(params.folderId !== undefined && { folderId: params.folderId }),
      },
    });
    this._lastCreatedDocumentId = documentId;
    return ok({ resourceId: documentId });
  }

  track(): ActionTrackMetadata | null {
    if (this._lastCreatedDocumentId === null) {
      return null;
    }
    return {
      event: CocoEvent.DocumentCreated,
      properties: { documentId: this._lastCreatedDocumentId },
    };
  }
}
